import { FC, useEffect, useState } from 'react';
import { FieldArray, Form, useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import * as Yup from 'yup';
import { Box } from '@mui/material';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { FormikDebounce } from '../common/form/FormikDebounce';
import { FormContainer } from '../common/form/FormContainer';
import { FormGroup } from '../common/form/FormGroup';
import { FieldWithAction } from '../common/form/FieldWithAction';
import { ResponsiveRow } from '../common/form/ResponsiveRow';
import { Input } from '../common/form/Input';
import { SubmitButton } from '../common/form/SubmitButton';
import { WorkplaceAutocomplete } from '../common/form/WorkplaceAutocomplete';
import { ActivityAutocomplete } from '../common/form/ActivityAutocomplete';
import { QcsButton } from '../common/basic/QcsButton';
import { QcsTypography } from '../common/basic/QcsTypography';
import QcsAlert from '../common/basic/QcsAlert';
import { ShowError } from '../ShowError';
import { ShowResourceContent } from '../ShowResourceContent';
import { apiClientService } from '@api/services/services';
import { resources } from '@api/resources';
import { ReferenceDto } from '@api/models/employees/ReferenceDto';
import { IntegrationVisitSettingsDto } from '@api/models/apiClient/IntegrationVisitSettingsDto';

const MAX_TYPE_LENGTH = 64;

interface WorkplaceRow {
  type: string;
  workplace: ReferenceDto | null;
}

interface FormValues {
  activity: ReferenceDto | null;
  workplaces: WorkplaceRow[];
}

//The BE compares the types trimmed and case-insensitively, so the FE check does too.
const normalizeType = (type?: string) => (type ?? '').trim().toUpperCase();

const toFormValues = (settings?: IntegrationVisitSettingsDto): FormValues => ({
  activity: settings?.activity ?? null,
  workplaces: settings?.workplaces ?? [],
});

const toDto = (values: FormValues): IntegrationVisitSettingsDto => ({
  activity: values.activity && { id: values.activity.id },
  workplaces: values.workplaces.map((x) => ({ type: x.type.trim(), workplace: { id: x.workplace!.id } })),
});

const validationSchema = (t: TFunction) =>
  Yup.object().shape({
    workplaces: Yup.array().of(
      Yup.object().shape({
        type: Yup.string()
          .trim()
          .required(t('errors.required')!)
          .test('uniqueType', (value, ctx) => {
            const rows: WorkplaceRow[] = ctx.from?.[1]?.value.workplaces ?? [];
            const isDuplicate = rows.filter((x) => normalizeType(x.type) === normalizeType(value)).length > 1;
            return isDuplicate ? ctx.createError({ message: t('errorCodes.320', { type: normalizeType(value) }) }) : true;
          }),
        workplace: Yup.object().nullable().required(t('errors.required')!),
      }),
    ),
  });

const WorkplaceRows: FC = () => {
  const { t } = useTranslation();
  const { values } = useFormikContext<FormValues>();

  return (
    <FieldArray name="workplaces">
      {({ push, remove }) => (
        <>
          {values.workplaces.map((_, index) => (
            <FieldWithAction
              key={index}
              action={
                <QcsButton onClick={() => remove(index)} sx={{ mt: 1 }}>
                  <DeleteForeverIcon color="error" />
                </QcsButton>
              }
            >
              <ResponsiveRow>
                <Input
                  name={`workplaces.${index}.type`}
                  label={t('integration.settings.visits.type')}
                  maxLength={MAX_TYPE_LENGTH}
                  fullWidth
                  required
                />
                <Box sx={{ width: '100%' }}>
                  <WorkplaceAutocomplete name={`workplaces.${index}.workplace`} label={t('integration.settings.visits.workplace')} required />
                </Box>
              </ResponsiveRow>
            </FieldWithAction>
          ))}
          {values.workplaces.length === 0 && <QcsAlert severity="warning">{t('integration.settings.visits.noWorkplacesWarning')}</QcsAlert>}
          <QcsButton variant="outlined" onClick={() => push({ type: '', workplace: null })} sx={{ alignSelf: 'flex-start' }}>
            {t('integration.settings.visits.addWorkplace')}
          </QcsButton>
        </>
      )}
    </FieldArray>
  );
};

const ActivityField: FC = () => {
  const { t } = useTranslation();
  const { values } = useFormikContext<FormValues>();

  return (
    <>
      <ActivityAutocomplete name="activity" label={t('integration.settings.visits.activity')} oneTimeEntry />
      {!values.activity && <QcsAlert severity="warning">{t('integration.settings.visits.noActivityWarning')}</QcsAlert>}
    </>
  );
};

/** The "Settings" tab: how the visits a partner system creates through the API access are filled in. */
export const IntegrationVisitSettings: FC = () => {
  const { t } = useTranslation();
  const [settings, setSettings] = useState<IntegrationVisitSettingsDto>();

  useEffect(() => {
    apiClientService.getIntegrationVisitSettings().then((res) => setSettings(res.data));
  }, []);

  //The BE returns the types as it stores them (trimmed, upper-cased), so the form shows those after saving.
  const handleSubmit = async (values: FormValues) => {
    const res = await apiClientService.updateIntegrationVisitSettings(toDto(values));
    setSettings(res.data);
  };

  return (
    <ShowResourceContent resource={resources.getIntegrationVisitSettings}>
      <FormikDebounce<FormValues>
        initialValues={toFormValues(settings)}
        validationSchema={validationSchema(t)}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        <Form style={{ marginTop: '1rem' }}>
          <FormContainer>
            <QcsTypography variant="h5">{t('integration.settings.visits.title')}</QcsTypography>
            <QcsTypography variant="body2">{t('integration.settings.visits.description')}</QcsTypography>

            <FormGroup name="integration.settings.visits.workplacesTitle">
              <QcsTypography variant="body2">{t('integration.settings.visits.workplacesDescription')}</QcsTypography>
              <WorkplaceRows />
            </FormGroup>

            <FormGroup name="integration.settings.visits.activityTitle">
              <QcsTypography variant="body2">{t('integration.settings.visits.activityDescription')}</QcsTypography>
              <ActivityField />
            </FormGroup>

            <ShowError resource={resources.updateIntegrationVisitSettings} />
            <SubmitButton />
          </FormContainer>
        </Form>
      </FormikDebounce>
    </ShowResourceContent>
  );
};
